import { createStore, del as idbDel, get as idbGet, keys as idbKeys, set as idbSet } from 'idb-keyval';
import { CheckoutPageProps } from '@/talons/checkoutPage/type';
import { removeFromStorage } from './storageFunctions';

/**
 * Order queue with state machine. Replaces the legacy single-blob
 * `offline_orders` / `synched_orders` arrays in localStorage. One record per
 * order, keyed by `order_number`. Read-modify-write race is structurally
 * impossible because no caller ever reads-or-writes the whole queue as a
 * single value.
 *
 * Loss scenarios this module exists to prevent (see orders-issues.md):
 *  - Suspect #1: concurrent overwrite when sync loop runs during checkout
 *  - Suspect #5: localStorage eviction on iOS Safari / low-memory Android
 *  - Suspect #6: silent QuotaExceededError on the shared localStorage bucket
 */

export type OrderState =
  | 'pending'    // never sent successfully; sync loop will POST it
  | 'inflight'   // POST in progress; a sync loop holds the lease
  | 'acked'      // /integra-pedido returned data.message; awaiting ERP confirmation
  | 'confirmed'  // pedido-notifica-get listed it; durable in the ERP
  | 'failed';    // exhausted MAX_ATTEMPTS; needs manual intervention

export interface OrderRecord {
  order_number: string;
  rca_cod_pessoa: string | null;
  state: OrderState;
  payload: CheckoutPageProps;
  attempts: number;
  created_at: number;
  last_attempt_at?: number;
  last_error?: string;
  inflight_lease_until?: number;
  acked_at?: number;
}

export interface OrdersBackend {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

const STORE_DB_NAME = 'pwa_rca_orders';
const STORE_OBJECT_NAME = 'orders';
const KEY_PREFIX = 'order:';
const MIGRATION_MARKER_KEY = '__migration_v1_done';
const LEGACY_OFFLINE_KEYS = ['@PWA_RCA:offline_orders', '@PWA_RCA:synched_orders'];
const DEFAULT_LEASE_MS = 2 * 60 * 1000;

const findEncodedLegacyKeys = (): string[] => {
  if (typeof localStorage === 'undefined') return [];
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      let decoded = k;
      try {
        decoded =
          typeof atob === 'function'
            ? new TextDecoder().decode(
                Uint8Array.from(atob(k), (c) => c.charCodeAt(0))
              )
            : k;
      } catch {
        decoded = k;
      }
      if (
        decoded.includes('offline_orders') ||
        decoded.includes('synched_orders')
      ) {
        out.push(k);
      }
    }
  } catch {
    // ignore — best-effort discovery
  }
  return out;
};

const orderKey = (orderNumber: string) => `${KEY_PREFIX}${orderNumber}`;

/**
 * IndexedDB-backed key/value store. We use `idb-keyval` instead of
 * localforage because:
 *   - we only need flat get/set/del/keys (no querying or indexing)
 *   - idb-keyval is ~600 bytes vs localforage's ~24 KB
 *   - it skips the WebSQL/localStorage fallback chain — modern PWA targets
 *     (iOS Safari, Android Chrome, desktop browsers) all ship IndexedDB.
 * If a device blocks IndexedDB entirely, the backend's getItem/setItem
 * will reject, the caller's try/catch path (e.g. enqueueOrder in
 * useCheckoutPage) refuses to declare success, and the user sees an error
 * rather than a silent loss.
 */
const createIdbKeyvalBackend = (): OrdersBackend => {
  const store = createStore(STORE_DB_NAME, STORE_OBJECT_NAME);
  return {
    getItem: async (key) => {
      const v = await idbGet<string>(key, store);
      return v ?? null;
    },
    setItem: async (key, value) => {
      await idbSet(key, value, store);
    },
    removeItem: async (key) => {
      await idbDel(key, store);
    },
    keys: async () => {
      const all = await idbKeys(store);
      // idb-keyval keys can be IDBValidKey (string | number | Date | ...);
      // we only ever write string keys, so this filter is a safety net.
      return all.filter((k): k is string => typeof k === 'string');
    },
  };
};

let backend: OrdersBackend | null = null;

/** Test seam — replace the backend before unit tests run. */
export const __setBackendForTests = (testBackend: OrdersBackend | null) => {
  backend = testBackend;
};

const getBackend = (): OrdersBackend => {
  if (!backend) backend = createIdbKeyvalBackend();
  return backend;
};

const readRecord = async (orderNumber: string): Promise<OrderRecord | null> => {
  const raw = await getBackend().getItem(orderKey(orderNumber));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as OrderRecord;
    if (!parsed || typeof parsed !== 'object' || !parsed.order_number) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeRecord = async (record: OrderRecord): Promise<void> => {
  await getBackend().setItem(orderKey(record.order_number), JSON.stringify(record));
};

const OFFLINE_VIEW_STATES: OrderState[] = ['pending', 'inflight', 'failed'];
const SYNCHED_VIEW_STATES: OrderState[] = ['acked', 'confirmed'];

export const listPayloadsForOfflineView = async (): Promise<CheckoutPageProps[]> => {
  const all = await listAllRecords();
  return all.filter((r) => OFFLINE_VIEW_STATES.includes(r.state)).map((r) => r.payload);
};

export const listPayloadsForSynchedView = async (): Promise<CheckoutPageProps[]> => {
  const all = await listAllRecords();
  return all.filter((r) => SYNCHED_VIEW_STATES.includes(r.state)).map((r) => r.payload);
};

export const isOrderSyncedInStore = async (orderNumber: string): Promise<boolean> => {
  const record = await readRecord(orderNumber);
  return record?.state === 'acked' || record?.state === 'confirmed';
};

export const purgeLegacyOrdersLocalStorage = (): void => {
  if (typeof localStorage === 'undefined') return;
  const keys = new Set([...LEGACY_OFFLINE_KEYS, ...findEncodedLegacyKeys()]);
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // best-effort
    }
  }
  try {
    removeFromStorage('offline_orders');
    removeFromStorage('synched_orders');
  } catch {
    // best-effort
  }
};

const listAllRecords = async (): Promise<OrderRecord[]> => {
  const keys = await getBackend().keys();
  const orderKeys = keys.filter((k) => k.startsWith(KEY_PREFIX));
  const records: OrderRecord[] = [];
  for (const k of orderKeys) {
    const raw = await getBackend().getItem(k);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as OrderRecord;
      if (parsed && parsed.order_number) records.push(parsed);
    } catch {
      // skip malformed
    }
  }
  return records;
};

// ─── module-level mutex ─────────────────────────────────────────────────────
//
// Serialises any whole-queue iteration (processPendingOrders /
// reconcileAckedOrders) so two concurrent triggers (route navigation +
// CHECK_INTERVAL + connectivity flip) cannot both walk the queue at once.
// Per-order safety is covered by acquireForSend's lease, but this lock keeps
// us from doing N+1 reads in parallel against the same IndexedDB.

let queueLock: Promise<void> = Promise.resolve();

const withQueueLock = async <T>(fn: () => Promise<T>): Promise<T> => {
  const previous = queueLock;
  let release: () => void = () => {};
  queueLock = new Promise<void>((resolve) => {
    release = resolve;
  });
  await previous;
  try {
    return await fn();
  } finally {
    release();
  }
};

// ─── public API ─────────────────────────────────────────────────────────────

/**
 * Persist a freshly-built order. Idempotent by `order_number` — re-enqueuing
 * the same order_number while it is already pending/inflight/acked is a no-op
 * (returns the existing record). This protects against the UI double-clicking
 * "Finalizar" or against an online-checkout retry path.
 */
export const enqueue = async (
  payload: CheckoutPageProps,
  options?: { rcaCodPessoa?: string | null }
): Promise<OrderRecord> => {
  const orderNumber = payload?.orders?.order_number;
  if (!orderNumber) {
    throw new Error('ordersStore.enqueue: payload.orders.order_number is required');
  }

  const existing = await readRecord(orderNumber);
  if (existing && existing.state !== 'failed') {
    return existing;
  }

  const record: OrderRecord = {
    order_number: orderNumber,
    rca_cod_pessoa: options?.rcaCodPessoa ?? null,
    state: 'pending',
    payload,
    attempts: 0,
    created_at: Date.now(),
  };
  await writeRecord(record);
  return record;
};

export const getRecord = (orderNumber: string): Promise<OrderRecord | null> =>
  readRecord(orderNumber);

export const listByState = async (state: OrderState): Promise<OrderRecord[]> => {
  const all = await listAllRecords();
  return all.filter((r) => r.state === state);
};

export const listAll = (): Promise<OrderRecord[]> => listAllRecords();

export const countsByState = async (): Promise<Record<OrderState, number>> => {
  const all = await listAllRecords();
  const counts: Record<OrderState, number> = {
    pending: 0,
    inflight: 0,
    acked: 0,
    confirmed: 0,
    failed: 0,
  };
  for (const r of all) counts[r.state] += 1;
  return counts;
};

/**
 * Atomically transition an order from `pending` to `inflight` with a time
 * lease. Returns the record on success, `null` if another caller already
 * holds the lease (or the order has moved past pending). Stale leases
 * (`now > inflight_lease_until`) are treated as expired so a crashed sync
 * loop cannot park an order forever.
 *
 * Note: IndexedDB operations are async, so two concurrent calls *can* both
 * read state=pending. The race is reconciled by re-reading after write and
 * bailing if the persisted lease doesn't match this caller — combined with
 * `withQueueLock`, this gives us single-writer semantics.
 */
export const acquireForSend = async (
  orderNumber: string,
  leaseMs: number = DEFAULT_LEASE_MS
): Promise<OrderRecord | null> => {
  const record = await readRecord(orderNumber);
  if (!record) return null;

  const now = Date.now();
  const leaseHeldByOther =
    record.state === 'inflight' &&
    record.inflight_lease_until !== undefined &&
    record.inflight_lease_until > now;
  if (leaseHeldByOther) return null;
  if (record.state !== 'pending' && record.state !== 'inflight') return null;

  const leaseUntil = now + leaseMs;
  const updated: OrderRecord = {
    ...record,
    state: 'inflight',
    last_attempt_at: now,
    inflight_lease_until: leaseUntil,
  };
  await writeRecord(updated);

  const verify = await readRecord(orderNumber);
  if (!verify || verify.inflight_lease_until !== leaseUntil) {
    return null;
  }
  return verify;
};

export const markAcked = async (orderNumber: string): Promise<void> => {
  const record = await readRecord(orderNumber);
  if (!record) return;
  const updated: OrderRecord = {
    ...record,
    state: 'acked',
    acked_at: Date.now(),
    inflight_lease_until: undefined,
    last_error: undefined,
  };
  await writeRecord(updated);
};

/**
 * The order is confirmed durable in the ERP. Transitions to a terminal
 * `confirmed` state and is retained in the store indefinitely so the
 * offline /order-status view can render the historical list (OLD-app parity
 * for `synched_orders` as an append-only log). Sticky terminal state — never
 * transitioned out of by the sync loop.
 */
export const markConfirmed = async (orderNumber: string): Promise<void> => {
  const record = await readRecord(orderNumber);
  if (!record) return;
  const updated: OrderRecord = {
    ...record,
    state: 'confirmed',
    inflight_lease_until: undefined,
    last_error: undefined,
  };
  await writeRecord(updated);
};

export const markPendingAgain = async (
  orderNumber: string,
  error?: string
): Promise<void> => {
  const record = await readRecord(orderNumber);
  if (!record) return;
  const updated: OrderRecord = {
    ...record,
    state: 'pending',
    attempts: record.attempts + 1,
    last_error: error,
    inflight_lease_until: undefined,
  };
  await writeRecord(updated);
};

export const markFailed = async (
  orderNumber: string,
  error?: string
): Promise<void> => {
  const record = await readRecord(orderNumber);
  if (!record) return;
  const updated: OrderRecord = {
    ...record,
    state: 'failed',
    last_error: error,
    inflight_lease_until: undefined,
  };
  await writeRecord(updated);
};

/**
 * Exponential backoff with a 1-hour cap. Pure function for testability.
 */
export const backoffDelayMs = (attempts: number): number => {
  const base = 30 * 1000;
  const cap = 60 * 60 * 1000;
  const raw = base * Math.pow(2, Math.max(0, attempts));
  return Math.min(raw, cap);
};

export const isReadyForRetry = (record: OrderRecord, now: number = Date.now()): boolean => {
  if (record.state !== 'pending') return false;
  if (record.attempts === 0 || !record.last_attempt_at) return true;
  return now - record.last_attempt_at >= backoffDelayMs(record.attempts);
};

// ─── migration from legacy single-blob storage ──────────────────────────────

/**
 * Move legacy `offline_orders` and `synched_orders` from localStorage into
 * the new per-order store. Runs once per device — protected by a marker
 * key. Tolerant of malformed JSON, encrypted entries (`ENCRYPT` constant),
 * and missing fields. We DO NOT delete the legacy keys until the migration
 * succeeds, so a crash mid-migration can be retried.
 */
export const migrateLegacyOrders = async (
  options?: { onError?: (err: unknown, context: string) => void }
): Promise<{ migrated: number; alreadyDone: boolean }> => {
  const onError = options?.onError ?? (() => {});
  const marker = await getBackend().getItem(MIGRATION_MARKER_KEY);
  if (marker === '1') {
    purgeLegacyOrdersLocalStorage();
    return { migrated: 0, alreadyDone: true };
  }

  let migrated = 0;

  const readLegacyArray = (storageKey: string): any[] => {
    if (typeof localStorage === 'undefined') return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = tryParseJsonOrEncoded(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      onError(err, `migrateLegacyOrders.read:${storageKey}`);
      return [];
    }
  };

  // legacy keys are stored under encrypted variants too — check both names
  // the storage layer uses the encoded key as the real localStorage key
  const candidateKeys = [
    ...LEGACY_OFFLINE_KEYS,
    ...findEncodedLegacyKeys(),
  ];

  // Bucket → target state mapping. Order matters: offline first (so a
  // duplicate order_number appearing in both buckets ends as 'acked').
  const buckets: Array<{ keys: string[]; targetState: OrderState }> = [
    {
      keys: candidateKeys.filter((k) => k.includes('offline_orders')),
      targetState: 'pending',
    },
    {
      keys: candidateKeys.filter((k) => k.includes('synched_orders')),
      targetState: 'acked',
    },
  ];

  for (const bucket of buckets) {
    for (const key of bucket.keys) {
      const items = readLegacyArray(key);
      for (const item of items) {
        try {
          const orderNumber = item?.orders?.order_number;
          if (!orderNumber || typeof orderNumber !== 'string') continue;

          const existing = await readRecord(orderNumber);
          if (existing) continue; // never overwrite a record already in the new store

          const record: OrderRecord = {
            order_number: orderNumber,
            rca_cod_pessoa: null,
            state: bucket.targetState,
            payload: item as CheckoutPageProps,
            attempts: 0,
            created_at: Date.now(),
            acked_at: bucket.targetState === 'acked' ? Date.now() : undefined,
          };
          await writeRecord(record);
          migrated += 1;
        } catch (err) {
          onError(err, `migrateLegacyOrders.write:${key}`);
        }
      }
    }
  }

  await getBackend().setItem(MIGRATION_MARKER_KEY, '1');

  // Only clear the legacy keys after the marker is set — if the marker
  // write fails we'll try again on next boot.
  for (const bucket of buckets) {
    for (const key of bucket.keys) {
      try {
        if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
      } catch (err) {
        onError(err, `migrateLegacyOrders.cleanup:${key}`);
      }
    }
  }

  purgeLegacyOrdersLocalStorage();

  return { migrated, alreadyDone: false };
};

const tryParseJsonOrEncoded = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    // legacy storageFunctions.ts uses base64 when ENCRYPT is set
    try {
      const decoded =
        typeof atob === 'function'
          ? new TextDecoder().decode(
              Uint8Array.from(atob(raw), (c) => c.charCodeAt(0))
            )
          : '';
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }
};

// ─── orchestration helpers (used by UserContext) ────────────────────────────

/**
 * Iterate every pending order ready for retry, acquire its lease, hand it
 * to `sendFn`, and transition state based on the result. Wrapped in the
 * queue lock so two parallel triggers don't double-walk.
 *
 * Idempotency contract for `sendFn`: callers MUST treat
 * `order_number` as the dedup key on the backend. Without that, retries
 * create duplicates. See Phase 2 risk in the plan.
 */
export const processPendingOrders = async (
  sendFn: (record: OrderRecord) => Promise<
    { ok: true } | { ok: false; error?: string; permanent?: boolean }
  >,
  options?: { maxAttempts?: number; rcaCodPessoa?: string | null }
): Promise<{ attempted: number; acked: number; deferred: number; failed: number }> => {
  const maxAttempts = options?.maxAttempts ?? 8;
  const rcaFilter = options?.rcaCodPessoa;

  return withQueueLock(async () => {
    const all = await listAllRecords();
    const pending = all.filter((r) => r.state === 'pending');
    const now = Date.now();

    let attempted = 0;
    let acked = 0;
    let deferred = 0;
    let failed = 0;

    for (const candidate of pending) {
      if (rcaFilter && candidate.rca_cod_pessoa && candidate.rca_cod_pessoa !== rcaFilter) {
        deferred += 1;
        continue;
      }
      if (!isReadyForRetry(candidate, now)) {
        deferred += 1;
        continue;
      }

      const leased = await acquireForSend(candidate.order_number);
      if (!leased) {
        deferred += 1;
        continue;
      }

      attempted += 1;
      let result: { ok: true } | { ok: false; error?: string; permanent?: boolean };
      try {
        result = await sendFn(leased);
      } catch (err) {
        result = { ok: false, error: err instanceof Error ? err.message : String(err) };
      }

      if (result.ok) {
        await markAcked(leased.order_number);
        acked += 1;
        continue;
      }

      const nextAttemptCount = leased.attempts + 1;
      if (result.permanent || nextAttemptCount >= maxAttempts) {
        await markFailed(leased.order_number, result.error);
        failed += 1;
      } else {
        await markPendingAgain(leased.order_number, result.error);
        deferred += 1;
      }
    }

    return { attempted, acked, deferred, failed };
  });
};

/**
 * For every order currently in state=acked, check whether the ERP has
 * recorded it via `pedido-notifica-get` (callback supplies the server-known
 * IDs). Confirmed orders are deleted; acked orders older than
 * `ackGraceMs` that are still missing from the server are reverted to
 * `pending` for retry. This is the replacement for `rescueStuckSynchedOrders`
 * — same idea, but actually rescues.
 */
export const reconcileAckedOrders = async (
  fetchServerKnownIds: () => Promise<Set<string>>,
  options?: { ackGraceMs?: number }
): Promise<{ confirmed: number; requeued: number; stillAcked: number }> => {
  const ackGraceMs = options?.ackGraceMs ?? 10 * 60 * 1000;

  return withQueueLock(async () => {
    const all = await listAllRecords();
    const acked = all.filter((r) => r.state === 'acked');
    if (acked.length === 0) {
      return { confirmed: 0, requeued: 0, stillAcked: 0 };
    }

    let serverIds: Set<string>;
    try {
      serverIds = await fetchServerKnownIds();
    } catch {
      // Can't reconcile without a server view — leave acked alone.
      return { confirmed: 0, requeued: 0, stillAcked: acked.length };
    }

    const now = Date.now();
    let confirmed = 0;
    let requeued = 0;
    let stillAcked = 0;

    for (const record of acked) {
      if (serverIds.has(record.order_number)) {
        await markConfirmed(record.order_number);
        confirmed += 1;
        continue;
      }
      const ackedAt = record.acked_at ?? record.last_attempt_at ?? record.created_at;
      if (now - ackedAt >= ackGraceMs) {
        await markPendingAgain(record.order_number, 'erp_confirmation_timeout');
        requeued += 1;
      } else {
        stillAcked += 1;
      }
    }

    return { confirmed, requeued, stillAcked };
  });
};

/**
 * Diagnostic helper — used by the debug screen and Sentry tags so support
 * can ask an RCA "how many orders are waiting on your device?".
 */
export const __resetForTests = async (): Promise<void> => {
  const keys = await getBackend().keys();
  for (const k of keys) {
    await getBackend().removeItem(k);
  }
  queueLock = Promise.resolve();
};
